import { Component, OnInit, HostListener, OnDestroy, ViewEncapsulation, viewChild, inject, computed } from '@angular/core';
import { GlobalEventListenerService } from './modules/core/services/global-event-listener.service';
import { Store } from '@ngrx/store';
import { withLatestFrom, takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import Environment from '@env';
import { LoggerService } from './modules/core/services/logger/logger.service';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { DEFAULT_APP_CULTURE_CODE } from './modules/core/languages/language.model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'topoone-web';
  tokenRefreshTimeout: any = null;
  tokenSubscription: Subscription = null;
  destroy$ = new Subject<void>();

  private userProfile = inject(fromUserProfile.UserProfileFacade);
  isSideNavEnabled = computed(() => {
    const userOrg = this.userProfile.userOrg();
    const sideNavEnabled = this.userProfile.sideNavEnabled();

    return !!userOrg && sideNavEnabled;
  });
  sideNav = viewChild('side_nav');

  constructor(
    private eventListenerService: GlobalEventListenerService,
    private store: Store<fromAuth.State>,
    private loggerService: LoggerService,
    private translateService: TranslateService
  ) {
    moment.relativeTimeThreshold('ss', 30);
    moment.relativeTimeThreshold('s', 30);
    moment.relativeTimeThreshold('h', 12);
    moment.relativeTimeThreshold('m', 30);
    moment.relativeTimeThreshold('d', 7);
    moment.relativeTimeThreshold('w', 4);
  }

  ngOnInit() {
    const loggerSettings = Environment.logging;
    this.loggerService.configure(loggerSettings);
    const logger = this.loggerService.getLogger('TopoONE');
    logger.logInfo('TopoONE is starting up...');

    // subscribe to token updates so that we can handle automated refresh
    this.tokenSubscription = this.store
      .select(fromAuth.selectToken)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(fromAuth.selectUserContext), this.store.select(fromUserProfile.selectProfileUser))
      )
      .subscribe(([token, userContext, userProfile]) => {
        if (!!userContext) {
          if (!!this.tokenRefreshTimeout) {
            clearTimeout(this.tokenRefreshTimeout);
          }

          // use fallback/org culture when dealing with inherited (shared) token
          const activeCultureCode =
            userContext.isInheritedToken === true ? userProfile?.culture?.fallbackCultureCode : userProfile?.culture?.cultureCode;

          if (!!activeCultureCode && this.translateService.currentLang !== activeCultureCode) {
            this.store.dispatch(
              fromUserProfile.changeTranslationCulture({
                cultureCode: activeCultureCode ?? DEFAULT_APP_CULTURE_CODE
              })
            );
          }

          const twoMinutes = 120000;
          const expDate = userContext.expiration * 1000;
          const expOffset = expDate - Date.now() - twoMinutes;

          if (expOffset < 0) {
            this.store.dispatch(fromAuth.refreshAccessToken());
            return;
          }

          this.tokenRefreshTimeout = setTimeout(() => {
            this.store.dispatch(fromAuth.refreshAccessToken());
          }, expOffset);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.tokenSubscription?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  documentClick(event: any): void {
    this.eventListenerService.clickEvents$.next(event.target);
  }
}
